import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCode,
  faImage,
  faSearch,
  faExchangeAlt,
  faFileAlt,
  faCog,
  faRocket,
  faChartLine,
  faMagic,
  faTools,
  faBolt,
  faShieldAlt,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import SEOHead from "../common/SEOHead";
import Reveal from "../components/Reveal";
import { trackEvent } from "../components/trackingUtils";
import { baseUrl } from "../config";
import "./Tools.css";

const tools = [
  {
    id: "seonavigator",
    name: "SEO Navigator",
    icon: faSearch,
    category: "SEO & Analytics",
    image: "/images/seonavigator.png",
    shortDescription: "Comprehensive SEO optimization and analysis toolkit",
    description:
      "SEO Navigator is a comprehensive tool designed to help businesses optimize their websites for search engines. It provides keyword performance insights, backlink analysis, on-page SEO audits, and technical site evaluations.",
    features: [
      "Keyword Analysis",
      "Backlink Monitoring",
      "Technical SEO Audit",
      "Competition Analysis",
    ],
    color: "from-blue-500 to-indigo-600",
    popular: true,
  },
  {
    id: "imageconverter",
    name: "Image Converter",
    icon: faImage,
    category: "Media Tools",
    image: "/images/ImageConverter.png",
    shortDescription: "Convert images to social media formats instantly",
    description:
      "Transform images into optimized formats for Twitter, Facebook, LinkedIn, and WhatsApp. Features drag-and-drop upload, real-time processing, and batch conversion capabilities.",
    features: [
      "Multi-Platform Support",
      "Drag & Drop Upload",
      "Batch Processing",
      "Instant Download",
    ],
    color: "from-green-500 to-emerald-600",
    popular: true,
  },
  {
    id: "comparexpert",
    name: "CompareXpert",
    icon: faExchangeAlt,
    category: "Development Tools",
    image: "/images/comparexpert.png",
    shortDescription: "Advanced text and JSON comparison with diff analysis",
    description:
      "Professional text and JSON comparison tool with intelligent diff algorithms, syntax highlighting, and comprehensive analysis for developers and content creators.",
    features: [
      "Text Comparison",
      "JSON Diff",
      "Syntax Highlighting",
      "Export Results",
    ],
    color: "from-purple-500 to-violet-600",
    popular: false,
  },
  {
    id: "tableiq",
    name: "TableIQ",
    icon: faFileAlt,
    category: "Data Analysis",
    image: "/images/tableiq.png",
    shortDescription: "Intelligent table analysis and data insights",
    description:
      "Advanced table analysis tool that provides intelligent insights, data visualization, and automated reporting for complex datasets and business intelligence.",
    features: [
      "Data Analysis",
      "Smart Insights",
      "Visual Reports",
      "Export Options",
    ],
    color: "from-orange-500 to-red-500",
    popular: false,
  },
  {
    id: "summarai",
    name: "SummarAI",
    icon: faMagic,
    category: "AI Tools",
    image: "/images/summarai.png",
    shortDescription: "AI-powered content summarization and analysis",
    description:
      "Leverage artificial intelligence to automatically summarize blogs, articles, and documents. Generate key insights and extract important information quickly.",
    features: [
      "AI Summarization",
      "Key Insights",
      "Multi-Format Support",
      "Quick Processing",
    ],
    color: "from-teal-500 to-cyan-600",
    popular: false,
  },
];

const categories = [
  { id: "all", name: "All Tools", icon: faTools },
  { id: "SEO & Analytics", name: "SEO & Analytics", icon: faChartLine },
  { id: "Media Tools", name: "Media Tools", icon: faImage },
  { id: "Development Tools", name: "Development Tools", icon: faCode },
  { id: "AI Tools", name: "AI Tools", icon: faMagic },
  { id: "Data Analysis", name: "Data Analysis", icon: faFileAlt },
];

const Tools = () => {
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const history = useHistory();

  useEffect(() => {
    trackEvent("PageLoad", "", "Tools");
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleCardClick = (id) => {
    trackEvent("Tools", "Click", id);
    history.push(`/tools/${id}`);
  };

  const filteredTools = tools.filter((tool) => {
    const matchesCategory =
      selectedCategory === "all" || tool.category === selectedCategory;
    const matchesSearch =
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.features.some((feature) =>
        feature.toLowerCase().includes(searchTerm.toLowerCase())
      );
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0b1220] via-[#1a2332] to-[#0b1220] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#E5C558] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Loading tools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b1220] via-[#1a2332] to-[#0b1220]">
      <SEOHead
        title="Professional Development Tools | GeniusNexa"
        description="Discover powerful tools for SEO optimization, image conversion, text comparison, AI summarization, and data analysis. Boost your productivity with our comprehensive toolkit."
        keywords="development tools, SEO tools, image converter, text comparison, AI tools, data analysis, GeniusNexa"
        url={`${baseUrl}/tools`}
        image={`${baseUrl}/images/tools-banner.jpg`}
      />

      {/* Hero Section */}
      <Reveal>
        <section className="relative py-20 px-4 text-center text-white">
          <div className="absolute inset-0">
            <div className="absolute w-96 h-96 bg-[#E5C558]/10 rounded-full blur-3xl top-0 right-0 animate-pulse"></div>
            <div
              className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl bottom-0 left-0 animate-pulse"
              style={{ animationDelay: "2s" }}
            ></div>
            <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          </div>
          <div className="relative max-w-6xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-6 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] rounded-full shadow-lg">
              <FontAwesomeIcon
                icon={faRocket}
                className="text-2xl text-[#0b1220]"
              />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Professional{" "}
              </span>
              <span className="bg-gradient-to-r from-[#E5C558] to-[#f5d56d] bg-clip-text text-transparent">
                Tools
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
              Discover our comprehensive suite of professional tools designed to
              enhance your productivity, optimize your workflow, and accelerate
              your success
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faBolt} />
                Lightning Fast
              </span>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faShieldAlt} />
                100% Secure
              </span>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCog} />
                Easy to Use
              </span>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faRocket} />
                Professional Grade
              </span>
            </div>
          </div>
        </section>
      </Reveal>

      {/* Search and Filter Section */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 mb-12 border border-white/20 shadow-2xl">
              <div className="flex flex-col lg:flex-row gap-6 items-center">
                {/* Search Bar */}
                <div className="flex-1 w-full">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <FontAwesomeIcon
                        icon={faSearch}
                        className="text-gray-400"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Search tools by name, description, or features..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent transition-all"
                    />
                  </div>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all duration-300 ${
                        selectedCategory === category.id
                          ? "bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220]"
                          : "bg-white/10 text-white hover:bg-white/20"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={category.icon}
                        className="text-sm"
                      />
                      <span className="hidden sm:inline">{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Results Summary */}
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-gray-300 text-center">
                  <span className="text-[#E5C558] font-semibold">
                    {filteredTools.length}
                  </span>{" "}
                  tools found
                  {selectedCategory !== "all" && (
                    <span>
                      {" "}
                      in{" "}
                      <span className="text-[#E5C558] font-semibold">
                        {selectedCategory}
                      </span>
                    </span>
                  )}
                  {searchTerm && (
                    <span>
                      {" "}
                      matching "
                      <span className="text-[#E5C558] font-semibold">
                        {searchTerm}
                      </span>
                      "
                    </span>
                  )}
                </p>
              </div>
            </div>
          </Reveal>

          {/* Tools Grid */}
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTools.map((tool) => (
                <div
                  key={tool.id}
                  onClick={() => handleCardClick(tool.id)}
                  className="group bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 shadow-2xl hover:shadow-[#E5C558]/20 hover:border-[#E5C558]/30 transition-all duration-300 cursor-pointer transform hover:scale-105"
                >
                  {/* Tool Header */}
                  <div className="relative mb-6">
                    {tool.popular && (
                      <div className="absolute -top-2 -right-2 px-3 py-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] text-xs font-bold rounded-full">
                        Popular
                      </div>
                    )}
                    <div
                      className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${tool.color} flex items-center justify-center mb-4 group-hover:shadow-lg transition-all duration-300`}
                    >
                      <FontAwesomeIcon
                        icon={tool.icon}
                        className="text-2xl text-white"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-white group-hover:text-[#E5C558] transition-colors">
                          {tool.name}
                        </h3>
                        <p className="text-sm text-gray-400 font-medium">
                          {tool.category}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tool Description */}
                  <p className="text-gray-300 mb-6 line-clamp-3 leading-relaxed">
                    {tool.shortDescription}
                  </p>

                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-white mb-3">
                      Key Features:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {tool.features.slice(0, 3).map((feature, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-white/10 text-gray-300 text-xs rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                      {tool.features.length > 3 && (
                        <span className="px-3 py-1 bg-[#E5C558]/20 text-[#E5C558] text-xs rounded-full">
                          +{tool.features.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="flex items-center justify-between">
                    <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-semibold rounded-xl hover:shadow-lg hover:shadow-[#E5C558]/30 transition-all duration-300 group-hover:scale-105">
                      <span>Try Now</span>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="text-sm group-hover:translate-x-1 transition-transform"
                      />
                    </button>
                    <div className="text-xs text-gray-400">Free to use</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* No Results */}
          {filteredTools.length === 0 && (
            <Reveal>
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-2xl text-gray-400"
                  />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  No tools found
                </h3>
                <p className="text-gray-400 mb-6">
                  Try adjusting your search terms or category filter.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory("all");
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-[#0b1220] font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  Clear Filters
                </button>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
};

export default Tools;
